import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { StoreOrderApi } from "../../order/StoreOrderApi";
import type { CreateStoreOrderResult } from "../../order/StoreOrderType";
import { spaceHomeHeroLayoutCssVars } from "../Config";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import { enabledStrategySpecs, pickDefaultStrategyId } from "../spaceProductPurchaseUtils";
import { SpaceProfileHero } from "./SpaceProfileHero";
import "../SpaceStyle.css";

export type SpaceProductPurchaseModalProps = {
  spaceId: string;
  row: MembershipProduct;
  open: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  spaceProfile: SpaceProfile | null;
  /** 策略市场模式预填 STRA */
  prefillStraId?: string;
  /** 独立站：左侧品牌区，不传则用 spaceProfile Hero */
  standaloneBrand?: {
    logoUrl: string;
    brandName: string;
    slogan: string;
    spaceIdLabel: string;
  };
  checkoutPath?: (orderId: string) => string;
};

export function SpaceProductPurchaseModal(props: SpaceProductPurchaseModalProps): JSX.Element {
  const {
    spaceId,
    row,
    open,
    onClose,
    isLoggedIn,
    onLoginRequest,
    spaceProfile,
    prefillStraId,
    standaloneBrand,
    checkoutPath,
  } = props;
  const navigate = useNavigate();
  const isStrategyTool = row.productKind === "strategy_tool";
  const isProductBundle = row.productKind === "product_bundle";
  const isMarket = isStrategyTool && row.strategySaleMode === "market";
  const isRetail = row.pricingMode?.trim().toLowerCase() === "retail";
  const bundleSpecs = useMemo(
    () => (row.priceSpecs ?? []).filter((s) => s.enabled),
    [row.priceSpecs]
  );
  const retailSpecs = isRetail ? row.priceSpecs ?? [] : [];
  const offers = useMemo(() => row.strategySkuOffers ?? [], [row.strategySkuOffers]);

  const [straId, setStraId] = useState<string | null>(null);
  const [tierId, setTierId] = useState<string | null>(null);
  const [pendingSign, setPendingSign] = useState(false);

  const specs = isMarket
    ? enabledStrategySpecs(offers, straId)
    : isStrategyTool
      ? bundleSpecs
      : retailSpecs.filter((s) => s.enabled);

  useEffect(() => {
    if (!open) return;
    if (isMarket) {
      const entitled =
        row.strategyEntitlements?.filter((e) => e.active).map((e) => e.subProductItemId) ?? [];
      const pick = pickDefaultStrategyId(offers, prefillStraId, entitled);
      setStraId(pick);
      const nextSpecs = enabledStrategySpecs(offers, pick);
      setTierId(nextSpecs[0]?.tierId ?? null);
      return;
    }
    setStraId(null);
    const list = isStrategyTool ? bundleSpecs : retailSpecs.filter((s) => s.enabled);
    setTierId(list.find((s) => s.enabled)?.tierId ?? list[0]?.tierId ?? null);
  }, [
    open,
    isMarket,
    isStrategyTool,
    prefillStraId,
    offers,
    bundleSpecs,
    retailSpecs,
    row.strategyEntitlements,
  ]);

  const profile = spaceProfile;
  const spaceName = profile?.name?.trim() ?? "";
  const avatarUrl = profile?.avatarUrl?.trim() ?? "";
  const displaySpaceId = profile?.spaceId ?? spaceId;
  const heroProfile = useMemo<SpaceProfile>(
    () =>
      profile ?? {
        spaceId: displaySpaceId,
        name: spaceName || row.displayName || "空间",
        avatarUrl,
        followerCount: 0,
        subscriptionCount: 0,
        certLabelName: null,
        signature: null,
        douyinId: null,
        douyinName: null,
      },
    [avatarUrl, displaySpaceId, profile, row.displayName, spaceName]
  );

  const maybeSignAndCreate = useCallback(async (): Promise<CreateStoreOrderResult | string> => {
    const tier = tierId ?? specs[0]?.tierId;
    if (!tier) {
      return "请选择规格";
    }
    if (isMarket && !straId) {
      return "请选择策略";
    }
    let res = await StoreOrderApi.createOrder({
      spaceId,
      spaceProductId: row.spaceProductId,
      serviceTier: tier,
      subProductItemId: isMarket ? straId ?? undefined : undefined,
    });
    if (typeof res !== "string" && res.createResult === 401) {
      const needSign = res;
      const tid = row.agreementTemplateId?.trim();
      if (!tid) {
        message.error(needSign.failMessage ?? "请先签署协议");
        return needSign;
      }
      await new Promise<void>((resolve) => {
        Modal.confirm({
          title: "签署购买协议",
          content: needSign.failMessage ?? "需先签署产品购买协议",
          okText: "去签署",
          cancelText: "取消",
          width: 400,
          centered: true,
          className: "lf-modal-square",
          onOk: async () => {
            const signRes = await StoreOrderApi.signAgreement({
              spaceId,
              spaceProductId: row.spaceProductId,
              templateId: tid,
            });
            if (typeof signRes === "string") {
              message.error(signRes);
            } else {
              message.success("签署成功");
            }
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
      res = await StoreOrderApi.createOrder({
        spaceId,
        spaceProductId: row.spaceProductId,
        serviceTier: tier,
        subProductItemId: isMarket ? straId ?? undefined : undefined,
      });
    }
    return res;
  }, [isMarket, row, spaceId, specs, straId, tierId]);

  const confirmPurchase = async () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (isMarket && !straId) {
      message.warning("请先选择策略");
      return;
    }
    if (specs.length > 0 && !tierId) {
      message.warning("请先选择规格");
      return;
    }
    setPendingSign(true);
    try {
      const res = await maybeSignAndCreate();
      if (typeof res === "string") {
        return;
      }
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      onClose();
      const path = checkoutPath
        ? checkoutPath(res.orderId)
        : `/checkout/${spaceId}/${encodeURIComponent(res.orderId)}`;
      navigate(path);
    } finally {
      setPendingSign(false);
    }
  };

  const showStrategyRow = isMarket && offers.length > 0;
  const showSpecRow = specs.length > 0 && (!isMarket || straId != null);

  const leadText = row.userSubscribed
    ? "您目前已经在订阅状态，延长权益周期，保持不间断会员权益"
    : isProductBundle
      ? "开通后将获得打包产品权益，可按套餐访问所含全部产品"
      : "开通后可按权益使用本空间会员服务";

  return (
    <Modal
      title={null}
      footer={null}
      closable={false}
      open={open}
      onCancel={onClose}
      width="100%"
      centered={false}
      destroyOnClose
      maskClosable
      className="space-purchase-modal space-purchase-modal--fullscreen"
      wrapClassName="space-purchase-modal-wrap"
      styles={{
        wrapper: { padding: 0 },
        content: {
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          width: "100vw",
          height: "100vh",
          borderRadius: 0,
          overflow: "hidden",
        },
        body: {
          padding: 0,
          height: "100%",
          overflow: "hidden",
        },
      }}
    >
      <div className="space-purchase-modal-shell">
        <aside className="space-purchase-modal-space-pane" aria-label="空间信息">
          {standaloneBrand ? (
            <div className="space-purchase-modal-space-pane__inner">
              <div className="space-purchase-modal-space-pane__avatar-wrap">
                <img
                  src={standaloneBrand.logoUrl}
                  alt=""
                  className="space-purchase-modal-space-pane__avatar"
                />
              </div>
              <div className="space-purchase-modal-space-pane__title-row">
                <span className="space-purchase-modal-space-pane__name">
                  {standaloneBrand.brandName}
                </span>
              </div>
              <p className="space-purchase-modal-space-pane__signature">
                {standaloneBrand.slogan}
              </p>
              <p className="space-purchase-modal-space-pane__fans">
                {standaloneBrand.spaceIdLabel}
              </p>
            </div>
          ) : (
            <div
              className="space-purchase-modal-space-pane__inner"
              style={spaceHomeHeroLayoutCssVars()}
            >
              <SpaceProfileHero
                profile={heroProfile}
                className="space-purchase-modal-space-pane__hero"
              />
            </div>
          )}
        </aside>
        <div className="space-purchase-modal-checkout-pane">
          <button
            type="button"
            className="space-purchase-modal-close"
            aria-label="关闭"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
          <div className="space-purchase-modal-checkout-scroll">
            <div className="space-purchase-modal-body__inner">
              <header className="space-purchase-modal-product-block">
                <p className="space-purchase-modal-product-title">{row.displayName}</p>
              </header>
              <section className="space-purchase-modal-intro" aria-label="产品介绍">
                {row.description != null && row.description.trim() !== "" ? (
                  <p>{row.description}</p>
                ) : (
                  <p className="space-purchase-modal-intro-placeholder">暂无详细介绍</p>
                )}
              </section>
              {showStrategyRow ? (
                <section className="space-purchase-modal-section" aria-label="策略">
                  <p className="space-purchase-modal-label">选择策略</p>
                  <div className="space-purchase-modal-tier-grid" role="group" aria-label="选择策略">
                    {offers.map((o) => {
                      const active = straId === o.subProductItemId;
                      return (
                        <button
                          key={o.subProductItemId}
                          type="button"
                          className={
                            active
                              ? "space-purchase-tier-card space-purchase-tier-card--active"
                              : "space-purchase-tier-card"
                          }
                          onClick={() => {
                            setStraId(o.subProductItemId);
                            const next = enabledStrategySpecs(offers, o.subProductItemId);
                            setTierId(next[0]?.tierId ?? null);
                          }}
                        >
                          <span className="space-purchase-tier-card__name">{o.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              {showSpecRow ? (
                <section className="space-purchase-modal-section" aria-label="规格">
                  <p className="space-purchase-modal-label">选择规格</p>
                  <div
                    className="space-purchase-modal-tier-grid"
                    role="group"
                    aria-label="选择规格"
                  >
                    {specs.map((s) => {
                      const active = tierId === s.tierId;
                      return (
                        <button
                          key={s.tierId}
                          type="button"
                          className={
                            active
                              ? "space-purchase-tier-card space-purchase-tier-card--active"
                              : "space-purchase-tier-card"
                          }
                          onClick={() => setTierId(s.tierId)}
                        >
                          <span className="space-purchase-tier-card__name">
                            {s.specName || s.durationLabel}
                          </span>
                          <span className="space-purchase-tier-card__price">¥{s.priceYuan}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              <p className="space-purchase-modal-product-lead">{leadText}</p>
            </div>
          </div>
          <div className="space-purchase-modal-footer-strip">
            <div className="space-purchase-modal-footer">
              <Button
                className="space-purchase-modal-footer-btn"
                style={{ minWidth: 120 }}
                onClick={onClose}
              >
                取消
              </Button>
              <Button
                type="primary"
                className="space-purchase-modal-footer-btn space-purchase-modal-footer-btn--primary"
                style={{ minWidth: 120 }}
                loading={pendingSign}
                onClick={() => void confirmPurchase()}
              >
                立即订阅
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
